import { useState, useCallback } from 'react'
import type { VideoGenerationResult, VideoGenerationOptions } from '@/types'

export function useVideoGenerator() {
  const [isGenerating, setIsGenerating] = useState(false)
  const [result, setResult] = useState<VideoGenerationResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)

  const generate = useCallback(async (options: VideoGenerationOptions): Promise<VideoGenerationResult | null> => {
    setIsGenerating(true)
    setError(null)
    setProgress(0)
    setResult(null)

    const startTime = Date.now()

    try {
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + Math.random() * 10, 85))
      }, 800)

      await new Promise(resolve => setTimeout(resolve, 3000))

      clearInterval(progressInterval)
      setProgress(100)

      const genTime = (Date.now() - startTime) / 1000

      const result: VideoGenerationResult = {
        url: "",
        prompt: options.prompt,
        ratio: options.ratio,
        style: options.style,
        model: options.model,
        duration: options.duration || 5,
        generationTime: genTime,
        timestamp: Date.now(),
        status: 'completed',
      }

      setResult(result)
      setIsGenerating(false)
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate video'
      setError(message)
      setIsGenerating(false)
      return null
    }
  }, [])

  const clear = useCallback(() => {
    setResult(null)
    setError(null)
    setProgress(0)
    setIsGenerating(false)
  }, [])

  return { generate, clear, isGenerating, result, error, progress }
}
